import { useMemo, useState, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { MainLayout } from '@/components/layout/MainLayout';
import { InfiniteScrollList } from '@/components/ui/infinite-scroll-list';
import { OmnisearchSelect } from '@/components/ui/omnisearch-select';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { StatusBadge } from '@/components/ui/status-badge';
import { authApi, customerApi, organizationApi } from '@/lib/pillarApi';
import type {
    ActiveRoleAssignmentResponse,
    MerchantCustomerRecordSearchListItem,
    OrganizationOmnisearchListItem,
    OrganizationOmnisearchPageResponse,
    MerchantTimeZone,
} from '@pillar-portal/api-types';
import { formatTimestampForMerchantUi, hasInternalAdminPrivileges } from '@pillar-portal/api-types';
import { Status } from '@pillar-portal/database/dist/frontend';
import { z } from 'zod';
import { getActiveRoleAssignmentOperationalResponse } from '@/lib/active-role-assignment';

type MerchantFilterSearchScope =
    | { organization_uuid: string; include_descendants: true }
    | Record<string, never>;

function formatDateTime(value: string | undefined, timeZone: MerchantTimeZone | null): string {
    if (value === undefined) {
        return '—';
    }
    return formatTimestampForMerchantUi(value, timeZone);
}

function formatPrimaryContact(customer: MerchantCustomerRecordSearchListItem): string {
    if (customer.primary_email !== undefined && customer.primary_email.length > 0) {
        return customer.primary_email;
    }
    if (customer.primary_phone !== undefined && customer.primary_phone.length > 0) {
        return customer.primary_phone;
    }
    return '—';
}

function renderMerchantFilterItem(organization: OrganizationOmnisearchListItem): ReactElement {
    const capacityText: string = organization.is_iso
        ? 'ISO'
        : organization.is_merchant
          ? 'Merchant'
          : 'Organization';
    return (
        <div className="space-y-1">
            <div className="font-medium">{organization.name}</div>
            <div className="text-xs text-muted-foreground">
                {capacityText} - {organization.organization_uuid}
            </div>
        </div>
    );
}

function buildMerchantFilterSearchScope(params: {
    canSearchAllMerchants: boolean;
    activeOrganizationUuid: string | undefined;
}): MerchantFilterSearchScope {
    if (params.canSearchAllMerchants) {
        return {};
    }
    if (params.activeOrganizationUuid === undefined) {
        throw new Error('Active organization is missing');
    }
    return {
        organization_uuid: params.activeOrganizationUuid,
        include_descendants: true,
    };
}

export function CustomersPageContent() {
    const navigate = useNavigate();
    const [showDeleted, setShowDeleted] = useState<boolean>(false);
    const [selectedMerchant, setSelectedMerchant] = useState<OrganizationOmnisearchListItem | null>(
        null
    );
    const queryKeyPrefix: ReadonlyArray<string> = useMemo(
        () => [
            'merchantCustomers',
            `merchant=${selectedMerchant?.organization_uuid ?? 'all'}`,
            `showDeleted=${showDeleted}`,
        ],
        [selectedMerchant?.organization_uuid, showDeleted]
    );
    const { data: activeRoleAssignmentResponse, isLoading: isActiveRoleAssignmentLoading } =
        useQuery<ActiveRoleAssignmentResponse | null>({
            queryKey: ['activeRoleAssignment'],
            queryFn: async () => await authApi.getActiveRoleAssignment(),
            staleTime: 30000,
        });
    const activeRoleAssignmentOperationalResponse = getActiveRoleAssignmentOperationalResponse(
        activeRoleAssignmentResponse
    );
    const canShowDeleted: boolean = hasInternalAdminPrivileges(
        activeRoleAssignmentOperationalResponse
    );
    const activeOrganizationUuid: string | undefined =
        activeRoleAssignmentOperationalResponse?.organization_uuid ?? undefined;
    const canSearchAllMerchants: boolean = canShowDeleted;
    const canSearchMerchantFilter: boolean =
        canSearchAllMerchants || activeOrganizationUuid !== undefined;
    const merchantFilterResetToken: string = canSearchAllMerchants
        ? 'merchant-scope=all'
        : `merchant-scope=${activeOrganizationUuid ?? 'missing'}`;

    if (isActiveRoleAssignmentLoading) {
        return <div>Loading...</div>;
    }

    return (
        <InfiniteScrollList<MerchantCustomerRecordSearchListItem, string, ReadonlyArray<string>>
            pageTitle="Customers"
            cardTitle="Customers"
            queryPlaceholder="Search customers..."
            queryKeyPrefix={queryKeyPrefix}
            pageSize={50}
            fetchPage={async (params) => {
                const response = await customerApi.searchCustomers({
                    ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
                    limit: params.limit,
                    cursor: params.cursor,
                    ...(selectedMerchant !== null
                        ? {
                              merchant_organization_uuid: selectedMerchant.organization_uuid,
                          }
                        : {}),
                    show_deleted: canShowDeleted && showDeleted ? true : undefined,
                });
                return {
                    items: response.merchant_customer_records,
                    pagination: response.pagination,
                };
            }}
            parseCursorParam={(pageParam: unknown) => {
                if (pageParam === null || pageParam === undefined) {
                    return undefined;
                }
                const parseResult = z.string().trim().min(1).safeParse(pageParam);
                if (!parseResult.success) {
                    throw new Error('Expected opaque cursor');
                }
                return parseResult.data;
            }}
            stateCopy={{
                loadingLabel: 'Loading customers...',
                emptyLabel: 'No customers found.',
                endOfListLabel: 'End of customers.',
                genericErrorLabel: 'Failed to load customers.',
            }}
            controls={
                <div className="flex items-end gap-4">
                    <div className="w-96">
                        <OmnisearchSelect<OrganizationOmnisearchListItem, string>
                            label="Merchant"
                            placeholder="Search merchants..."
                            pageSize={50}
                            selectedItemId={selectedMerchant?.organization_uuid ?? null}
                            disabled={!canSearchMerchantFilter}
                            resetToken={merchantFilterResetToken}
                            fetchPage={async (params) => {
                                const scopeParams = buildMerchantFilterSearchScope({
                                    canSearchAllMerchants,
                                    activeOrganizationUuid,
                                });
                                const response: OrganizationOmnisearchPageResponse =
                                    await organizationApi.searchOrganizations({
                                        ...(params.terms !== undefined
                                            ? { terms: [...params.terms] }
                                            : {}),
                                        is_merchant: true,
                                        ...scopeParams,
                                        limit: params.limit,
                                        cursor: params.cursor,
                                    });
                                return {
                                    items: response.organizations,
                                    pagination: response.pagination,
                                };
                            }}
                            getItemId={(item) => item.organization_uuid}
                            getItemLabel={(item) => item.name}
                            getItemFilterText={(item) =>
                                `${item.name} ${item.organization_uuid}`.toLowerCase()
                            }
                            renderItemCard={renderMerchantFilterItem}
                            onSelect={(item) => {
                                setSelectedMerchant(item);
                            }}
                            onClearSelection={() => {
                                setSelectedMerchant(null);
                            }}
                        />
                    </div>
                    {canShowDeleted ? (
                        <div className="flex items-center gap-2 pb-2">
                            <Label htmlFor="customer-show-deleted" className="cursor-pointer">
                                Show deleted
                            </Label>
                            <Checkbox
                                id="customer-show-deleted"
                                checked={showDeleted}
                                onCheckedChange={(checked) => {
                                    setShowDeleted(checked === true);
                                }}
                            />
                        </div>
                    ) : null}
                </div>
            }
            renderItems={({ items }) => {
                const visibleCustomers: ReadonlyArray<MerchantCustomerRecordSearchListItem> =
                    items.filter((customer) =>
                        showDeleted
                            ? customer.status === Status.DELETED
                            : customer.status !== Status.DELETED
                    );

                if (visibleCustomers.length === 0) {
                    return <p className="text-sm text-gray-600">No customers found.</p>;
                }

                return (
                    <div className="space-y-2">
                        {visibleCustomers.map((customer) => (
                            <button
                                key={customer.merchant_customer_record_uuid}
                                type="button"
                                className="w-full rounded-lg border p-4 text-left transition-colors hover:bg-gray-50"
                                onClick={() =>
                                    navigate(`/customers/${customer.merchant_customer_record_uuid}`)
                                }
                            >
                                <div className="flex items-start justify-between gap-6">
                                    <div className="min-w-0 space-y-1">
                                        <div className="flex items-center gap-2">
                                            <h3 className="font-semibold">{customer.name}</h3>
                                            <StatusBadge status={customer.status} />
                                        </div>
                                        <p className="text-sm text-muted-foreground">
                                            Contact: {formatPrimaryContact(customer)}
                                        </p>
                                        <p className="text-sm text-muted-foreground">
                                            Organization:{' '}
                                            {customer.customer_organization_name ?? '—'}
                                        </p>
                                    </div>
                                    <div className="space-y-1 text-right text-sm">
                                        <p className="text-muted-foreground">
                                            Last request:{' '}
                                            {customer.last_payment_request_reference_number ?? '—'}
                                        </p>
                                        <p className="text-muted-foreground">
                                            Last activity:{' '}
                                            {formatDateTime(
                                                customer.last_payment_activity_at,
                                                customer.merchant_timezone
                                            )}
                                        </p>
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                );
            }}
        />
    );
}

export default function CustomersPage() {
    return (
        <ProtectedRoute>
            <MainLayout>
                <CustomersPageContent />
            </MainLayout>
        </ProtectedRoute>
    );
}
